import { Logger } from '@/utils/Logger';
import React, { Component, ReactNode } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import Button from '../Button/Button';
import styles from './ErrorBoundaryMorePrecised.module.css';

class ErrorBoundaryMorePrecised extends Component<
  { children: ReactNode } & WithTranslation,
  { hasError: boolean; error?: Error; errorInfo?: React.ErrorInfo }
> {
  constructor(props: { children: ReactNode } & WithTranslation) {
    super(props);
    this.state = { hasError: false, error: undefined, errorInfo: undefined };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Logger.error('ErrorBoundary', 'Uncaught render error', error, errorInfo);
    this.setState({ errorInfo });
  }

  highlightStackLine(line: string) {
    const filePathRegex = /(\/[\w\-./]+:\d+:\d+)/g;
    const parts = line.split(filePathRegex);

    return parts.map((part, i) => {
      if (filePathRegex.test(part)) {
        return (
          <span key={i} style={{ color: '#9cdcfe' }}>
            {part}
          </span>
        );
      } else if (/^\s*at /.test(part)) {
        return (
          <span key={i} style={{ color: '#dcdcaa' }}>
            {part}
          </span>
        );
      } else {
        return <span key={i}>{part}</span>;
      }
    });
  }

  reset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  redirectToHome = () => {
    window.location.href = '/';
  };

  render() {
    const { t } = this.props;

    if (this.state.hasError) {
      return (
        <>
          <div className={styles.container}>
            <h2 className={styles.title}>{t('errorBoundary.title', 'Oops, something went wrong.')}</h2>
            <details className={styles.details} open>
              <summary className={styles.summary}>{this.state.error?.toString()}</summary>

              <div className={styles.stackTrace}>
                <strong className={styles.stackTraceTitle}>{t('errorBoundary.jsStackTrace', 'JS Stack trace:')}</strong>
                <pre className={styles.stackTraceContent}>
                  {this.state.error?.stack?.split('\n').map((line, i) => (
                    <div key={i} className={styles.line}>
                      {this.highlightStackLine(line)}
                    </div>
                  ))}
                </pre>
              </div>

              <div>
                <strong className={styles.componentStackTitle}>
                  {t('errorBoundary.reactComponentStack', 'React component stack:')}
                </strong>
                <pre className={styles.componentStackContent}>{this.state.errorInfo?.componentStack}</pre>
              </div>
            </details>
          </div>
          <div className={styles.buttonsBoundary}>
            <Button onClick={this.reset}>{t('main.error.reloadWidget')}</Button>
          </div>
        </>
      );
    }
    return this.props.children;
  }
}

export default withTranslation()(ErrorBoundaryMorePrecised);
